// components/profile/ProfileModal.jsx  (파일명 바꾸는 걸 추천)
// 기존 파일을 이 코드로 교체하세요.
import { useEffect, useState, useMemo } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";

const ISSUE_FIELDS = [
    { key: "has_hypertension", label: "고혈압" },
    { key: "has_hypotension", label: "저혈압" },
    { key: "has_diabetes", label: "당뇨" },
    { key: "has_heart_disease", label: "심장병" },
    { key: "has_fatty_liver", label: "지방간" },
    { key: "has_heart_failure", label: "심부전" },
    { key: "has_arthritis", label: "관절염" },
    { key: "has_hypoglycemia", label: "저혈당" },
    { key: "has_anemia", label: "빈혈" },
];

function toNumOrNull(v) {
    return v === "" || v === null || typeof v === "undefined" ? null : Number(v);
}

export default function ProfileModal({
    open,
    onClose,
    profile,           // 서버에서 가져온 프로필 (없으면 undefined/null)
    onSubmit,          // (payload) => Promise<void> | void
    pending = false,   // 저장 중일 때 버튼 상태
    error = null,      // 에러 메시지(선택)
}) {
    const [form, setForm] = useState({
        height_cm: "",
        weight_kg: "",
        has_hypertension: false,
        has_hypotension: false,
        has_diabetes: false,
        has_heart_disease: false,
        has_fatty_liver: false,
        has_heart_failure: false,
        has_arthritis: false,
        has_hypoglycemia: false,
        has_anemia: false,
    });

    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setForm((s) => ({
            ...s,
            height_cm: profile?.height_cm ?? "",
            weight_kg: profile?.weight_kg ?? "",
            has_hypertension: !!profile?.has_hypertension,
            has_hypotension: !!profile?.has_hypotension,
            has_diabetes: !!profile?.has_diabetes,
            has_heart_disease: !!profile?.has_heart_disease,
            has_fatty_liver: !!profile?.has_fatty_liver,
            has_heart_failure: !!profile?.has_heart_failure,
            has_arthritis: !!profile?.has_arthritis,
            has_hypoglycemia: !!profile?.has_hypoglycemia,
            has_anemia: !!profile?.has_anemia,
        }));
    }, [open, profile]);

    if (!open) return null;

    const handleChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const handleToggle = (k) => setForm((s) => ({ ...s, [k]: !s[k] }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);

        const payload = {
            height_cm: toNumOrNull(form.height_cm),
            weight_kg: toNumOrNull(form.weight_kg),
            has_hypertension: form.has_hypertension,
            has_hypotension: form.has_hypotension,
            has_diabetes: form.has_diabetes,
            has_heart_disease: form.has_heart_disease,
            has_fatty_liver: form.has_fatty_liver,
            has_heart_failure: form.has_heart_failure,
            has_arthritis: form.has_arthritis,
            has_hypoglycemia: form.has_hypoglycemia,
            has_anemia: form.has_anemia,
        };

        console.log("[ProfileModal] payload:", payload);
        console.table(
            Object.fromEntries(
                Object.entries(payload).map(([k, v]) => [k, { value: v, type: typeof v }])
            )
        );

        try {
            await onSubmit?.(payload);
        } catch (err) {
            const msg = err?.message || err?.data?.detail || "저장 중 오류가 발생했습니다.";
            setSubmitError(msg);
        }
    };

    const title = "건강 기초 정보 입력";

    return (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            <form
                onSubmit={handleSubmit}
                className="
                    relative w-[92vw] max-w-[720px] md:max-w-[550px]
                    rounded-lg bg-white shadow-xl px-6 py-6 md:p-6
                    max-h-[85vh] overflow-y-auto
                    "
            >
                <div className="relative flex items-center justify-center mb-4">
                    <h2 className="absolute left-1/2 -translate-x-1/2 text-xl md:text-2xl font-bold">
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="닫기"
                        className="ml-auto p-1 rounded-md active:scale-95"
                    >
                        <XMarkIcon className="w-7 h-7 text-gray-500" />
                    </button>
                </div>

                {/* 키 / 몸무게 */}
                <div className="mb-5">
                    <div className="text-base md:text-lg font-semibold mb-2 text-left">키 / 몸무게</div>

                    {/* 전체를 가로 중앙정렬 */}
                    <div
                        className="
                            grid w-full justify-center items-center gap-4
                            grid-cols-[minmax(110px,35%)_auto_minmax(110px,35%)_auto]
                            "
                    >
                        {/* 키 */}
                        <div>
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                value={form.height_cm ?? ""}
                                onChange={(e) => handleChange("height_cm", e.target.value)}
                                placeholder="163"
                                aria-label="키"
                                className="
                                w-full
                               rounded-lg bg-gray-100
                                px-4 py-3
                                text-center text-base md:text-lg font-medium text-black
                                outline-none focus:ring-2 focus:ring-[#5F9BFF]
                                [appearance:textfield]
                                [&::-webkit-outer-spin-button]:appearance-none
                                [&::-webkit-inner-spin-button]:appearance-none
                                "
                            />
                        </div>
                        <div className="text-base md:text-lg select-none text-center">cm</div>

                        {/* 몸무게 */}
                        <div>
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                value={form.weight_kg ?? ""}
                                onChange={(e) => handleChange("weight_kg", e.target.value)}
                                placeholder="49"
                                aria-label="몸무게"
                                className="
                                w-full
                                rounded-lg bg-gray-100
                                px-4 py-3
                                text-center text-base md:text-lg font-medium text-black
                                outline-none focus:ring-2 focus:ring-[#5F9BFF]
                                [appearance:textfield]
                                [&::-webkit-outer-spin-button]:appearance-none
                                [&::-webkit-inner-spin-button]:appearance-none
                                "
                            />
                        </div>
                        <div className="text-base md:text-lg select-none text-center">kg</div>
                    </div>
                </div>


                {/* 특이사항 */}
                <div className="mb-3">
                    <div className="text-sm md:text-base font-semibold mb-2">특이사항</div>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-2 px-4">
                        {ISSUE_FIELDS.map(({ key, label }) => (
                            <label key={key} className="inline-flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={!!form[key]}
                                    onChange={() => handleToggle(key)}
                                    className="h-4 w-4 rounded border-gray-300 text-[#5F9BFF] focus:ring-[#5F9BFF]"
                                />
                                <span className="text-sm md:text-base">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>


                {(error || submitError) && (
                    <p className="text-sm text-red-500 mb-2">{String(error?.message || error)}</p>
                )}

                <button
                    type="submit"
                    disabled={pending}
                    className="mt-2 w-full rounded-lg px-4 py-2.5 text-white text-base md:text-lg
                     bg-[#5F9BFF] hover:brightness-95 active:scale-[.99]"
                >
                    {pending ? "변경 내용 저장" : "변경 내용 저장"}
                </button>
            </form>
        </div>
    );
}
